/**
 * @linkhr/hr-domain/document-request — Zod schemas for document requests.
 *
 * Phase 8 wires two document types on /me/payroll (Decision D-11, D-12):
 *   - SALARY_CERTIFICATE (backend enum, corresponds to "Salary Letter" UX)
 *
 * NOTE: The plan (08-02) assumed documentType values "TAX_CERTIFICATE" and
 * "SALARY_LETTER" but the actual backend schema (CC-40 / CreateDocumentRequestRequest
 * in schema.d.ts) uses "EMPLOYMENT_LETTER" | "SALARY_CERTIFICATE" |
 * "EXPERIENCE_CERTIFICATE" | "NOC_TRAVEL" | "OTHER". This schema aligns to the
 * actual backend contract — see project conventions "Backend contract discipline".
 *
 * The tax certificate equivalent in Phase 8 scope is SALARY_CERTIFICATE.
 * Full REQ-03 employee self-service documents (Employment letter, Experience cert,
 * NOC travel doc types) are deferred to the REQ-03 phase per D-12.
 *
 * Mirrors `components["schemas"]["CreateDocumentRequestRequest"]` from
 * the generated OpenAPI schema.
 */

import { z } from "zod";

/**
 * All document types supported by the backend (CC-40).
 * Phase 8 wires only SALARY_CERTIFICATE; others are present for completeness
 * and forward-compat.
 */
export const DOCUMENT_TYPES = [
  "EMPLOYMENT_LETTER",
  "SALARY_CERTIFICATE",
  "EXPERIENCE_CERTIFICATE",
  "NOC_TRAVEL",
  "OTHER",
] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

/**
 * Letter options for EMPLOYMENT_LETTER and SALARY_CERTIFICATE requests.
 * Mirrors `components["schemas"]["LetterOptions"]` from schema.d.ts:10111–10119.
 */
export const LetterOptions = z.object({
  purposeCode: z.enum(["BANK", "EMBASSY", "RENTAL", "COURT", "SCHOOL", "OTHER"]),
  language: z.enum(["EN", "AR", "BOTH"]),
  showSalary: z.boolean(),
  includeStamp: z.boolean(),
  additionalNotes: z.string(),
});

export type LetterOptions = z.infer<typeof LetterOptions>;

/**
 * Document request view status values.
 * Aligned to backend schema.d.ts:10133 (CC-40).
 *
 * NOTE: Previously included "PROCESSING" (not a backend value) and was
 * missing "APPROVED" + "CHANGES_REQUESTED" — those silently coerced to
 * "PENDING" via .catch() masking real status. Now aligned to the backend
 * enum: PENDING | APPROVED | REJECTED | CHANGES_REQUESTED | ISSUED | CANCELLED.
 */
export const DOC_REQUEST_STATUS_VALUES = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CHANGES_REQUESTED",
  "ISSUED",
  "CANCELLED",
] as const;

export type DocumentRequestStatus = (typeof DOC_REQUEST_STATUS_VALUES)[number];

/**
 * Document request view — returned by list/detail endpoints.
 * Uses `.passthrough()` and `.catch("PENDING")` on status for forward-compat.
 */
export const DocumentRequestView = z
  .object({
    id: z.string().optional(),
    employeeId: z.string().optional(),
    documentType: z.enum(DOCUMENT_TYPES).optional(),
    status: z.enum(DOC_REQUEST_STATUS_VALUES).catch("PENDING"),
    purpose: z.string().optional(),
    deliveryPreference: z.string().optional(),
    issuedDocumentKey: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

export type DocumentRequestView = z.infer<typeof DocumentRequestView>;
